import React, { useState } from 'react';
import { Container, Divider, Stack, Grid, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { PropertySummaryView } from './property-summary-view';
import { ViewPropertyContentProps } from '../../types/viewPropertyContentProps';
import defaultBuildingImage from '../../../../assets/images/defaultBuilding.jpg';
import CopyIcon from '@mui/icons-material/CopyAllRounded';
import useCopyToClipboard from '../../../../ui/helpers/copy-to-clipboard';
import { Notifier } from '../../../../system/services/notificationManager';
import { useAuthenticator } from '../../../../auth/AuthenticationContext';
import { AuthorizedUser } from '../../../../auth/models';
import { useEffect } from 'react';

export function PropertyLocationContent({ data }: ViewPropertyContentProps) {
  const {
    _id,
    title,
    address,
    propMgr,
    owner,
    maintMgr,
    imageUrl,
    organization,
  } = data || {};
  const { line1, city, state, postalCode } = address || {};
  const addressLine = `${line1 || 'Unknown Address'}`;
  const cityStateZip = `${city || 'Unknown City'}, ${state || '??'}  ${
    postalCode || '00000'
  }`;
  const fullAddress = `${addressLine}, ${cityStateZip}`;
  const [clipboardValue, copyToClipboard] = useCopyToClipboard();
  const auth = useAuthenticator();
  const [user, setUser] = React.useState<AuthorizedUser>();
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setUser(auth?.user ? auth.user : undefined);
  }, [auth]);

  // Reset image error state when imageUrl changes
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  return (
    <Grid container sx={{ px: '10px' }}>
      <Grid item xs={12} sm={8} md={8} lg={8} sx={{ mx: '0px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <h3>Property Information</h3>
          </div>
          {user?.isSuper && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <IconButton
                aria-label="copy to clipboard"
                onClick={async () => {
                  if (_id) {
                    await copyToClipboard(_id);
                    Notifier.success('Property ID copied to clipboard');
                    console.log('Copied to clipboard', clipboardValue);
                  } else {
                    Notifier.error(
                      'No Property ID available to copy to clipboard',
                    );
                  }
                }}
              >
                <Tooltip title="Click to copy Property ID">
                  <CopyIcon />
                </Tooltip>
              </IconButton>
            </div>
          )}
        </div>
        <Stack direction="column" sx={{ px: '24px' }}>
          <div>
            <span style={{ fontWeight: 'bold' }}>Title: </span>
            <span style={{ textTransform: 'capitalize' }}>{title}</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold' }}>Address: </span>
            <span style={{ textTransform: 'capitalize' }}>{fullAddress}</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold' }}>Owning Organization: </span>
            <span>{organization?.name || 'Unspecified'}</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold' }}>Manager: </span>
            <span>{propMgr?.name ?? 'N/A'}</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold' }}>Maintenance Manager: </span>
            <span>{maintMgr?.name ?? 'N/A'}</span>
            <span> / </span>
            <span>{maintMgr?.mobile ?? 'N/A'}</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold' }}>Owner: </span>
            <span>{owner?.name ?? 'N/A'}</span>
          </div>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={4}>
        <Container sx={{ pt: '20px' }}>
          <img
            src={
              imageError || !imageUrl
                ? defaultBuildingImage
                : imageUrl.toString()
            }
            alt={imageError || !imageUrl ? 'Default Property' : 'Your Property'}
            style={{ width: '100%', height: 'auto' }}
            onError={() => setImageError(true)}
            onLoad={() => {
              // Reset error state when a new image successfully loads
              if (imageError && imageUrl) {
                setImageError(false);
              }
            }}
          />
        </Container>
      </Grid>
      <Grid item sm={12} md={12} lg={12} sx={{ my: '10px' }}>
        <Divider
          orientation="horizontal"
          flexItem
          style={{ borderColor: 'rgba(0, 0, 0, 0.5)' }}
        />
      </Grid>
      <Grid container sx={{ p: '0px' }}>
        <PropertySummaryView data={data} />
      </Grid>
    </Grid>
  );
}
